import { CalendarOffIcon } from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { isSmallScreen } from '@/core/utils/responsive';
import React from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import { usePropertyLabels } from '../hooks/usePropertyLabels';

export interface CancelReservationDialogProps {
  visible: boolean;
  onNo: () => void;
  onYes: () => void;
  isConfirming?: boolean;
}

/** Figma cancel-reservation dialog frame (desktop) */
const DIALOG_WIDTH = 513;
const DIALOG_HEIGHT = 290;
/** (513 − 48 padding − 16 button gap) / 2 */
const ACTION_BUTTON_WIDTH = 224.5;
/** Mobile frame — 314×258, gap 24, radius 16, padding Spacing/6 (24) */
const MOBILE_DIALOG_WIDTH = 314;
const MOBILE_DIALOG_HEIGHT = 258;
const MOBILE_DIALOG_GAP = 24;
/** Icon → “Cancel Reservation” title */
const ICON_TO_TITLE_GAP = 16;
const ICON_SIZE_DESKTOP = 80;
/** Mobile — smaller so header + wrapping message fit */
const ICON_SIZE_MOBILE = 48;

/** Web: height must include padding (otherwise 290 + 24 → 314). */
const webBorderBox: ViewStyle =
  Platform.OS === 'web'
    ? ({ boxSizing: 'border-box' } as ViewStyle)
    : {};

/**
 * Confirm cancel unit reservation (web properties).
 * Figma desktop: 513×290, radius 16, padding 24, gap 32 — No | Yes.
 * Mobile: smaller icon, message wraps (no nowrap crop).
 */
export function CancelReservationDialog({
  visible,
  onNo,
  onYes,
  isConfirming = false,
}: Readonly<CancelReservationDialogProps>) {
  const colors = useUIColors();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const isMobile = isSmallScreen();
  const iconSize = isMobile ? ICON_SIZE_MOBILE : ICON_SIZE_DESKTOP;

  const handleYes = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.RESERVE,
      actionName: ALMETLAA_ACTION.RESERVE.CANCEL_CONFIRM,
      eventType: 'action',
    });
    onYes();
  };

  return (
    <ContentDialog
      visible={visible}
      onClose={onNo}
      closeOnBackdropPress={!isConfirming}
      contentAlign="center"
      maxWidth={isMobile ? MOBILE_DIALOG_WIDTH : DIALOG_WIDTH}
      cardStyle={[
        styles.card,
        isMobile ? styles.cardMobile : styles.cardDesktop,
        webBorderBox,
        { backgroundColor: colors.backgroundLevel1 },
      ]}
      headerStyle={styles.header}
      actionsStyle={styles.actions}
      subtitleStyle={[
        styles.subtitle,
        isMobile ? styles.subtitleMobile : styles.subtitleDesktop,
      ]}
      icon={
        <View style={styles.iconBlock}>
          <CalendarOffIcon size={iconSize} color={colors.fontPrimary} />
        </View>
      }
      title={labels.CANCEL_RESERVATION_TITLE}
      titleVariant={isMobile ? 'headline' : 'title2'}
      titleColor="primary"
      titleStyle={isMobile ? styles.titleMobile : styles.title}
      subtitle={labels.CANCEL_RESERVATION_MESSAGE}
      subtitleVariant={isMobile ? 'body' : 'bodyLarge'}
      subtitleColor="secondary"
      actions={[
        {
          label: labels.CANCEL_RESERVATION_NO,
          variant: 'outline',
          flex: true,
          disabled: isConfirming,
          style: [
            styles.noButton,
            isMobile ? styles.actionButtonMobile : null,
            { backgroundColor: colors.backgroundLevel1 },
          ],
          onPress: () => {
            trackCatalogEvent({
              screen: ALMETLAA_SCREEN.RESERVE,
              actionName: ALMETLAA_ACTION.RESERVE.CANCEL_DIALOG_NO,
              eventType: 'action',
            });
            onNo();
          },
        },
        {
          label: labels.CANCEL_RESERVATION_YES,
          variant: 'primary',
          flex: true,
          disabled: isConfirming,
          style: [
            styles.yesButton,
            isMobile ? styles.actionButtonMobile : null,
          ],
          onPress: handleYes,
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: DIALOG_WIDTH,
    gap: 32,
    borderRadius: 16,
    paddingTop: 24,
    paddingRight: 24,
    paddingBottom: 24,
    paddingLeft: 24,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardDesktop: {
    height: DIALOG_HEIGHT,
    maxHeight: DIALOG_HEIGHT,
    overflow: 'hidden',
  },
  /** Mobile Figma: 314×258, gap 24, radius 16, padding Spacing/6 */
  cardMobile: {
    width: MOBILE_DIALOG_WIDTH,
    maxWidth: MOBILE_DIALOG_WIDTH,
    height: MOBILE_DIALOG_HEIGHT,
    maxHeight: MOBILE_DIALOG_HEIGHT,
    gap: MOBILE_DIALOG_GAP,
    borderRadius: 16,
    padding: 24,
    overflow: 'hidden',
  },
  header: {
    gap: 8,
    alignItems: 'center',
    width: '100%',
    flexShrink: 1,
  },
  iconBlock: {
    marginBottom: ICON_TO_TITLE_GAP,
  },
  /** Desktop — Size/2XL Medium, LH 100%, centered */
  title: {
    lineHeight: 22,
    textAlign: 'center',
    fontWeight: '500',
  },
  /** Mobile — Font/Size/Base SemiBold, LH 100%, centered */
  titleMobile: {
    lineHeight: 16,
    textAlign: 'center',
  },
  /** Size/M Regular, LH 100%, centered */
  subtitle: {
    textAlign: 'center',
    alignSelf: 'center',
    width: '100%',
    lineHeight: 18,
    /** Title → message */
    marginTop: 8,
  },
  /** Desktop Figma — single line */
  subtitleDesktop: {
    ...(Platform.OS === 'web'
      ? ({ whiteSpace: 'nowrap' } as ViewStyle)
      : null),
  },
  /** Mobile — 14 Regular, LH 21, wrap so “reservation” isn’t cropped */
  subtitleMobile: {
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    lineHeight: 21,
    textAlign: 'center',
    flexShrink: 1,
    ...(Platform.OS === 'web'
      ? ({ whiteSpace: 'normal' } as ViewStyle)
      : null),
  },
  actions: {
    justifyContent: 'center',
    flexWrap: 'nowrap',
    gap: 16,
    width: '100%',
    flexShrink: 0,
  },
  noButton: {
    maxWidth: ACTION_BUTTON_WIDTH,
    width: '100%',
    height: 48,
    gap: 4,
    borderRadius: 4,
    borderWidth: 2,
    padding: 12,
  },
  yesButton: {
    maxWidth: ACTION_BUTTON_WIDTH,
    width: '100%',
    height: 48,
    gap: 4,
    borderRadius: 4,
    padding: 12,
  },
  actionButtonMobile: {
    maxWidth: '100%',
  },
});

export default CancelReservationDialog;
